import React from 'react';

import { AppstoreFilled, BarsOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Form, Input, Radio, RadioChangeEvent, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { SystemDictionarySlugs } from '@app/models/products';
import { useLocalDispatch, useLocalSelector } from '@app/services/hooks/useRedux';
import { setFilters } from '@app/store/productsReducer';
import { TableControlsContainer } from '@components/TableControlsContainer';

import { DictionarySelect } from './components/DictionarySelect';
import { FilterEditMenu } from './components/FilterEditMenu';

import { Filters } from './interfaces';

interface IProps {
  isLoading?: boolean;
  isViewSwitchVisible?: boolean;
  maxTagCount?: 'responsive' | number;
}

export const SearchControls: React.FC<IProps> = ({ isLoading, isViewSwitchVisible = true, maxTagCount }) => {
  const filterSearchString = useLocalSelector((state) => state.products.filterSearchString);
  const filterTags = useLocalSelector((state) => state.products.filterTags);
  const filterCategories = useLocalSelector((state) => state.products.filterCategories);
  const filterBrands = useLocalSelector((state) => state.products.filterBrands);
  const { token } = theme.useToken();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearchState] = React.useState(filterSearchString);
  const [selectedFilters, setSelectedFilters] = React.useState<Filters[]>([]);
  const dispatch = useLocalDispatch();
  const nomenclatureListT = useTranslation(NSKeys.nomenclatureList);
  const commonT = useTranslation(NSKeys.common);
  const FILTERS = [
    { value: Filters.CATEGORIES, label: nomenclatureListT.t('searchCategories') },
    { value: Filters.TAGS, label: nomenclatureListT.t('searchTags') },
    { value: Filters.BRANDS, label: nomenclatureListT.t('searchBrands') },
  ];
  const [activeListType, setActiveListType] = React.useState('grid');

  React.useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const urlListType = params.get('listType');

    if (!urlListType) setSearchParams({ listType: activeListType });
    if (urlListType) setActiveListType(urlListType);

    return () => {
      dispatch(setFilters({ filterSearchString: '', filterTags: [], filterCategories: [], filterBrands: [] }));
    };
  }, []);

  React.useEffect(() => setSearchState(filterSearchString), [filterSearchString]);

  const handleSearchClick = (): void => {
    dispatch(
      setFilters({
        filterSearchString: search,
        ...(!selectedFilters.find((el) => el === Filters.CATEGORIES) && { filterCategories: [] }),
        ...(!selectedFilters.find((el) => el === Filters.TAGS) && { filterTags: [] }),
        ...(!selectedFilters.find((el) => el === Filters.BRANDS) && { filterBrands: [] }),
      })
    );
  };

  const handleReset = () => {
    setSelectedFilters([]);
    if (filterTags.length || filterCategories.length || filterBrands.length || filterSearchString) {
      setSearchState('');
      dispatch(setFilters({ filterSearchString: '', filterTags: [], filterCategories: [], filterBrands: [] }));
    }
  };

  const handleListTypeChange = (e: RadioChangeEvent) => {
    setActiveListType(e.target.value);

    setSearchParams({ listType: e.target.value });
  };

  return (
    <TableControlsContainer
      showBadge={!!filterTags.length || !!filterCategories.length || !!filterBrands.length || !!filterSearchString}>
      <React.Fragment>
        <Flex justify="end" wrap="wrap" gap={16} style={{ marginBottom: 16 }}>
          <FilterEditMenu filters={FILTERS} selectedFilters={selectedFilters} onChange={setSelectedFilters} />

          <Input.Search
            style={{ maxWidth: 240 }}
            size="middle"
            value={search}
            onChange={(e) => {
              setSearchState(e.target.value);
            }}
            onPressEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSearchClick();
            }}
            placeholder={nomenclatureListT.t('searchButton')}
            enterButton
            disabled={isLoading}
            onSearch={handleSearchClick}
          />

          {isViewSwitchVisible && (
            <Radio.Group value={activeListType} buttonStyle="solid" onChange={handleListTypeChange}>
              <Radio.Button value="table">
                <BarsOutlined />
              </Radio.Button>
              <Radio.Button value="grid">
                <AppstoreFilled twoToneColor={token.colorPrimary} />
              </Radio.Button>
            </Radio.Group>
          )}
        </Flex>
        {!!selectedFilters.length && (
          <Flex gap={16} wrap="wrap">
            {selectedFilters.map((filter) => (
              <Col style={{ width: 250 }} key={`filter-${filter}`}>
                <Form.Item style={{ marginBottom: 0 }}>
                  {filter === Filters.CATEGORIES && (
                    <DictionarySelect
                      maxTagCount={maxTagCount}
                      value={filterCategories}
                      placeholder={nomenclatureListT.t('searchCategories')}
                      dictionaryName={SystemDictionarySlugs.Categories}
                      onSelect={(data) => dispatch(setFilters({ filterCategories: data }))}
                    />
                  )}
                  {filter === Filters.TAGS && (
                    <DictionarySelect
                      maxTagCount={maxTagCount}
                      value={filterTags}
                      placeholder={nomenclatureListT.t('searchTags')}
                      dictionaryName={SystemDictionarySlugs.Tags}
                      onSelect={(data) => dispatch(setFilters({ filterTags: data }))}
                    />
                  )}
                  {filter === Filters.BRANDS && (
                    <DictionarySelect
                      maxTagCount={maxTagCount}
                      value={filterBrands}
                      placeholder={nomenclatureListT.t('searchBrands')}
                      dictionaryName={SystemDictionarySlugs.Brands}
                      onSelect={(data) => dispatch(setFilters({ filterBrands: data }))}
                    />
                  )}
                </Form.Item>
              </Col>
            ))}

            <Form.Item style={{ marginBottom: 10 }}>
              <Button type="link" icon={<DeleteOutlined />} onClick={handleReset}>
                {commonT.t('buttonReset')}
              </Button>
            </Form.Item>
          </Flex>
        )}
      </React.Fragment>
    </TableControlsContainer>
  );
};
